import PropTypes from "prop-types";
import { Button, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import apiClient from "../../helpers/apiClient";
import TopBack from "../reservations/components/subcompdash/Topback";
import LocationFormModal from "./LocationFormModal";
import { useCategoriesContext } from "../../contexts/CategoriesContext";
import { useToast } from "../../contexts/ToastContext";
import { DataTable, useTableFilters, RowActionsMenu } from "../../components/data-table";

export default function Locations({ embedded = false }) {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const { removeLocation, updateLocationCount } = useCategoriesContext();

    const [limit, setLimit] = useState(10);
    const [datas, setDatas] = useState([]);
    const [initialFetching, setInitialFetching] = useState(true);
    const [activePageNumber, setActivePageNumber] = useState(1);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const table = useTableFilters({ persistKey: "locations", onChange: () => setActivePageNumber(1) });
    const searchParams = table.filters;
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setInitialFetching(true);
            const queryParams = new URLSearchParams({
                limit: limit.toString(),
                page: activePageNumber.toString(),
                ...searchParams,
            });
            const response = await apiClient.get(`/api/location/list?${queryParams}`);

            if (response?.data?.success) {
                const totalItemsNum = response?.data?.meta?.total || 0;
                setTotalItemCount(totalItemsNum);
                setDatas(response?.data?.data || []);
                updateLocationCount(totalItemsNum);
            }
        } catch (_error) {
            setDatas([]);
            setTotalItemCount(0);
        } finally {
            setInitialFetching(false);
        }
    }, [activePageNumber, limit, searchParams, updateLocationCount]);

    const deleteLocation = async (locationId) => {
        try {
            const response = await apiClient.delete(`/api/location/${locationId}`);
            if (response?.data?.success) {
                removeLocation(locationId);
                toast.success({
                    message: t("locations.successfullyDeletedLocation"),
                });
                fetchData();
            }
        } catch (_error) {
            toast.error({
                message: t("locations.failedToDelete"),
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openCreateModal = () => {
        setEditingLocation(null);
        setModalOpen(true);
    };

    const openEditModal = (location) => {
        setEditingLocation(location);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingLocation(null);
    };

    const handleDelete = (locationId) => {
        Modal.confirm({
            title: t("locations.confirmDelete"),
            okText: t("delete"),
            cancelText: t("rates.cancel"),
            centered: true,
            okButtonProps: {
                danger: true,
            },
            onOk: () => deleteLocation(locationId),
        });
    };

    const renderStateCell = (location) => {
        if (location.isInsuranceEnabled) {
            return (
                <div className="flex gap-1 items-center">
                    <BsShieldFillCheck style={{ color: "#22c55e", fontSize: "0.9rem" }} title="Insurance enabled" />
                    <span>{location.state}</span>
                </div>
            );
        }

        return (
            <div className="flex gap-1 items-center">
                <BsShieldFillExclamation
                    style={{ color: "#f59e0b", fontSize: "0.9rem" }}
                    title="Insurance not enabled"
                />
                <span>{location.state}</span>
            </div>
        );
    };

    const columns = [
        {
            title: t("locations.address"),
            dataIndex: "address",
            ellipsis: true,
            ...table.getColumnSearchProps("address"),
            render: (address) => <span className="text-sm">{address || "N/A"}</span>,
        },
        {
            title: t("locations.city"),
            dataIndex: "city",
            width: 180,
            ...table.getColumnSearchProps("city"),
            render: (city) => <span className="text-sm">{city || "N/A"}</span>,
        },
        {
            title: t("locations.state"),
            dataIndex: "state",
            width: 180,
            ...table.getColumnSearchProps("state"),
            render: (_, location) => renderStateCell(location),
        },
        {
            title: t("locations.country"),
            dataIndex: "country",
            width: 160,
            ...table.getColumnSearchProps("country"),
            render: (country) => <span className="text-sm">{country || "N/A"}</span>,
        },
        {
            title: t("locations.totalCars"),
            dataIndex: "cars",
            width: 120,
            align: "center",
            render: (cars) => <span className="text-sm tabular-nums">{cars?.length ?? 0}</span>,
        },
        {
            title: <span className="sr-only">{t("action")}</span>,
            key: "action",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, location) => {
                const actionItems = [
                    { label: t("edit"), key: "edit" },
                    { label: t("delete"), key: "delete", danger: true },
                ];

                const onClickAction = ({ key }) => {
                    if (key === "edit") {
                        openEditModal(location);
                        return;
                    }
                    if (key === "delete") {
                        handleDelete(location.locationId);
                    }
                };

                return (
                    <RowActionsMenu items={actionItems} onClick={onClickAction} label={t("action")} />
                );
            },
        },
    ];

    const dataSource = datas.map((location) => ({
        ...location,
        key: location.locationId,
    }));

    // data-tour anchors the whole locations page for the product tour, which uses it to
    // introduce per-location operational hours and time zones. It wraps the heading, the
    // list and the pagination so the spotlight covers everything the step talks about.
    const pageContent = (
        <div data-tour="locations-page">
            <DataTable
                title={t("locations.locations")}
                count={totalItemCount}
                headerExtra={
                    <Button icon={<PlusOutlined />} onClick={openCreateModal}>
                        {t("locations.createLocation")}
                    </Button>
                }
                filters={table}
                search={{ key: "searchTerm", placeholder: t("locationSearchPlaceholder") }}
                filterLabels={{
                    address: t("locations.address"),
                    city: t("locations.city"),
                    state: t("locations.state"),
                    country: t("locations.country"),
                }}
                columns={columns}
                dataSource={dataSource}
                loading={initialFetching}
                rowKey="locationId"
                scroll={{ x: 1000 }}
                pagination={{
                    total: totalItemCount,
                    current: activePageNumber,
                    pageSize: limit,
                    onChange: setActivePageNumber,
                    onPageSizeChange: setLimit,
                }}
            />

            <LocationFormModal
                open={modalOpen}
                location={editingLocation}
                onCancel={closeModal}
                onSuccess={() => {
                    fetchData();
                }}
            />
        </div>
    );

    if (embedded) {
        return pageContent;
    }

    return (
        <>
            <TopBack backLocation={"/cars"} />
            <div>
                {pageContent}
            </div>
        </>
    );
}

Locations.propTypes = {
    embedded: PropTypes.bool,
};
